import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Lock, Eye, Bell, Camera, Send, UploadCloud, Sun, Moon } from "lucide-react";
import { Tip } from "./Tip";
import { useTheme } from "next-themes";
import { Button } from "../components/ui/button";
import { MemberAvatar } from "./LockScreen";
import { tr } from "../lib/i18n";
import { isNativeApp } from "../lib/native";

export const PlanBadge = ({ plan, trial = false, daysLeft = 0 }) => isNativeApp() ? null : (
  <Link
    to="/pricing"
    data-testid="plan-badge"
    title={trial ? `Plus trial — ${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "Plans & billing"}
    className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
      plan === "free" && !trial ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"
    }`}
  >
    {(plan !== "free" || trial) && <Sparkles className="h-3 w-3" />}
    {trial && plan === "free" ? <>plus <span className="normal-case opacity-80">· trial {daysLeft}d</span></> : plan}
  </Link>
);

export const MemberChip = ({ member, readOnly, onClick }) =>
  member ? (
    <button
      data-testid="member-chip"
      onClick={onClick}
      title={readOnly ? "Read-only access" : "Signed in"}
      className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border bg-card pl-1 pr-2.5 py-0.5 hover:border-primary transition-colors"
    >
      <MemberAvatar member={member} size="h-6 w-6 text-[10px]" />
      <span className="text-xs font-medium max-w-[90px] truncate hidden 2xl:inline">{member.name}</span>
      {member.role === "admin" && <span className="rounded-full bg-primary/15 text-primary px-1.5 text-[9px] font-mono uppercase tracking-wider" data-testid="member-chip-admin-badge">Admin</span>}
      {readOnly && <Eye className="h-3 w-3 text-muted-foreground" />}
    </button>
  ) : null;

export const SecurityButtons = ({ security, onOpenSecurity }) => (
  <>
    <Tip label="Security & settings — PIN, family members, recovery email, digest, yearly check-up, email delivery, activity log">
      <Button data-testid="security-trigger-btn" variant="ghost" size="icon" aria-label={tr("Security & settings")} onClick={onOpenSecurity} className="rounded-full relative">
        <ShieldCheck className="h-5 w-5" />
        {security?.status && !security.status.pin_set && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-500" />}
      </Button>
    </Tip>
    {security?.status?.pin_set && security.unlocked && (
      <Tip label={tr("Lock the vault now or switch to another family member")}>
        <Button data-testid="navbar-lock-btn" variant="ghost" size="icon" aria-label={tr("Lock the vault")} onClick={security.lockNow} className="rounded-full hidden sm:inline-flex">
          <Lock className="h-5 w-5" />
        </Button>
      </Tip>
    )}
  </>
);

export const RemindersButton = ({ badge, onClick }) => (
  <Tip label={tr("Reminders — renewals, warranties and tax dates pulled from your documents")}>
  <Button data-testid="reminders-trigger-btn" variant="ghost" size="icon" aria-label={tr("Reminders")} onClick={onClick} className="rounded-full relative">
    <Bell className="h-5 w-5" />
    {badge > 0 && (
      <span
        data-testid="reminders-badge"
        className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-mono font-semibold grid place-items-center"
      >
        {badge}
      </span>
    )}
  </Button>
  </Tip>
);

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  return (
    <Tip label={tr("Switch light / dark theme")}>
      <Button data-testid="theme-toggle-btn" variant="ghost" size="icon" aria-label={tr("Switch light / dark theme")} onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="rounded-full">
        <Sun className="h-5 w-5 dark:hidden" />
        <Moon className="h-5 w-5 hidden dark:block" />
      </Button>
    </Tip>
  );
};

export const CaptureActions = ({ onCamera, onForward, onUpload, readOnly }) => {
  if (readOnly) return null;
  return (
    <>
      {onCamera && (
        <Tip label={tr("Scan a receipt or letter with your camera — edges detected, page straightened, filed by AI")}>
          <Button data-testid="camera-trigger-btn" variant="outline" onClick={onCamera} className="rounded-full border-border gap-2">
            <Camera className="h-4 w-4" />
            <span className="hidden 2xl:inline">{tr("Snap")}</span>
          </Button>
        </Tip>
      )}
      {onForward && (
        <Tip label={tr("Email or text a document in — your vault inbox address and the text sandbox live here")}>
          <Button data-testid="forwarding-modal-trigger" variant="outline" onClick={onForward} className="hidden md:inline-flex rounded-full border-border gap-2">
            <Send className="h-4 w-4" />
            <span className="hidden 2xl:inline">{tr("Forward")}</span>
          </Button>
        </Tip>
      )}
      {onUpload && (
        <Tip label={tr("Upload PDFs, photos or scans — AI names, files and extracts dates and amounts")}>
          <Button data-testid="upload-trigger-btn" onClick={onUpload} className="rounded-full gap-2 bg-primary hover:bg-primary/90 shadow-sm">
            <UploadCloud className="h-4 w-4" />
            <span className="hidden sm:inline">{tr("Upload")}</span>
          </Button>
        </Tip>
      )}
    </>
  );
};
